import { Injectable, inject, signal } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { Router } from '@angular/router';
import { firstValueFrom, take } from 'rxjs';
import { ApiService } from './api.service';
import { API } from './constants';

type PushPermission = NotificationPermission | 'unsupported';

@Injectable({ providedIn: 'root' })
export class PushNotificationService extends ApiService {
  private readonly swPush = inject(SwPush);
  // `router` is private on CoreService, so we resolve our own here under a
  // distinct name to navigate from notification click handlers.
  private readonly nav = inject(Router);

  // Reactive permission state so UI banners can react to enable()/disable().
  readonly permission = signal<PushPermission>(this.detectInitialPermission());
  readonly subscribed = signal<boolean>(false);
  private clickSubscribed = false;

  isSupported(): boolean {
    return (
      this.swPush.isEnabled &&
      typeof Notification !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator
    );
  }

  /**
   * One-shot opt-in: requests browser permission, fetches the server VAPID key,
   * subscribes via SwPush, and posts the resulting endpoint to the backend so
   * future server-side events can reach this device.
   */
  async enable(): Promise<boolean> {
    if (!this.isSupported()) {
      this.permission.set('unsupported');
      return false;
    }
    if (Notification.permission === 'denied') {
      this.permission.set('denied');
      return false;
    }

    const permission =
      Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    this.permission.set(permission);
    if (permission !== 'granted') return false;

    const keyRes = await this.get<{ publicKey: string }>(API.pushVapidPublicKey);
    const publicKey = keyRes.data?.publicKey;
    if (!publicKey) return false;

    let subscription: PushSubscription;
    try {
      subscription = await this.swPush.requestSubscription({
        serverPublicKey: publicKey
      });
    } catch {
      return false;
    }

    const payload = subscription.toJSON() as {
      endpoint: string;
      keys?: { p256dh: string; auth: string };
    };
    if (!payload.endpoint || !payload.keys?.p256dh || !payload.keys?.auth) {
      return false;
    }

    const userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined;
    const res = await this.post(API.pushSubscribe, {
      body: {
        endpoint: payload.endpoint,
        keys: { p256dh: payload.keys.p256dh, auth: payload.keys.auth },
        userAgent
      }
    });
    if (res.error) {
      // Roll back the SW subscription so the user can retry cleanly instead
      // of being stuck with a half-registered endpoint that the server never
      // sees.
      try {
        await subscription.unsubscribe();
      } catch {
        // ignore — best-effort cleanup
      }
      return false;
    }

    this.subscribed.set(true);
    this.installClickHandler();
    return true;
  }

  async disable(): Promise<boolean> {
    if (!this.isSupported()) return false;
    // `swPush.subscription` is a long-lived Subject that never completes, so
    // `.toPromise()` hangs forever. `firstValueFrom + take(1)` resolves on
    // the next emission and then unsubscribes.
    const subscription = await firstValueFrom(
      this.swPush.subscription.pipe(take(1))
    ).catch(() => null);
    const endpoint = subscription?.endpoint;
    try {
      await this.swPush.unsubscribe();
    } catch {
      // even if the browser already lost it, keep going so the backend row gets removed
    }
    if (endpoint) {
      await this.delete(API.pushUnsubscribe, { body: { endpoint } });
    }
    this.subscribed.set(false);
    return true;
  }

  /**
   * Wires up the SW's notificationClicks stream so tapping a push notification
   * brings the user to the deep link the backend put in `data.url`. Safe to
   * call multiple times — installs once.
   */
  installClickHandler(): void {
    if (this.clickSubscribed) return;
    if (!this.isSupported()) return;
    this.clickSubscribed = true;
    this.swPush.notificationClicks.subscribe((click) => {
      const url = (click.notification?.data as any)?.url as string | undefined;
      if (url) this.nav.navigateByUrl(url);
    });
  }

  /**
   * Reads the live SW subscription state so consumers can render the banner
   * idempotently after a page reload (the browser remembers; our signal does
   * not survive a reload).
   */
  async refreshSubscriptionState(): Promise<void> {
    if (!this.isSupported()) {
      this.permission.set('unsupported');
      this.subscribed.set(false);
      return;
    }
    this.permission.set(Notification.permission);
    // Same fix as in `disable()`: the Subject never completes, so we must
    // grab the first emission and bail out instead of waiting for it to end.
    const sub = await firstValueFrom(
      this.swPush.subscription.pipe(take(1))
    ).catch(() => null);
    this.subscribed.set(!!sub);
    if (sub) this.installClickHandler();
  }

  private detectInitialPermission(): PushPermission {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  }
}
